import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useCart } from '../context/CartContext';
import { useWishlist } from '../context/WishlistContext';
import { useToast } from '../context/ToastContext';
import { useBreakpoint } from '../hooks/useBreakpoint';
import ProductCard from '../components/ProductCard';
import ReviewSection from '../components/ReviewSection';
import SEOHead from '../components/SEOHead';
import api from '../api/axios';
import { trackEvent } from '../utils/recommendationEngine';
import { getProductImageUrl } from '../utils/productImage';

const SITE_URL = process.env.REACT_APP_SITE_URL || 'https://www.a1looks.com';
const JUTTI_SIZES = ['36', '37', '38', '39', '40', '41', '42'];
const JUTTI_SIZE_GUIDE = [
  { eu: '36', cm: '22.5', inch: '8.9"' },
  { eu: '37', cm: '23.0', inch: '9.1"' },
  { eu: '38', cm: '23.5', inch: '9.3"' },
  { eu: '39', cm: '24.0', inch: '9.4"' },
  { eu: '40', cm: '24.5', inch: '9.6"' },
  { eu: '41', cm: '25.0', inch: '9.8"' },
  { eu: '42', cm: '26.0', inch: '10.2"' },
];
const C = { primary: '#8B1A1A', gold: '#D4A017' };

const CATEGORY_TITLE_SUFFIX = {
  juttis: 'Handcrafted Punjabi Jutti Online',
  earrings: 'Ethnic Earrings Online',
  necklaces: 'Kundan Necklace Online',
  bracelets: 'Ethnic Bangle Set Online',
  rings: 'Ethnic Rings Online',
  'potli-bags': 'Ethnic Potli Bag Online',
};

export default function ProductDetail() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { addToCart: addToCartFn } = useCart();
  const { isWishlisted, toggleWishlist: toggleWishlistFn } = useWishlist();
  const { addToast } = useToast();
  const { isMobile } = useBreakpoint();
  const [product, setProduct] = useState(null);
  const [related, setRelated] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedImage, setSelectedImage] = useState('');
  const [selectedSize, setSelectedSize] = useState('');
  const [qty, setQty] = useState(1);
  const [added, setAdded] = useState(false);
  const [showSizeGuide, setShowSizeGuide] = useState(false);

  useEffect(() => {
    setLoading(true);
    api.get(`/products/${slug}/`)
      .then(res => {
        setProduct(res.data);
        setSelectedImage(getProductImageUrl(res.data));
        trackEvent(res.data.category.slug, 'product_view');
        return api.get(`/products/?category=${res.data.category.slug}&page_size=4`);
      })
      .then(res => setRelated((res.data.results || res.data).filter(p => p.slug !== slug)))
      .catch(() => navigate('/shop'))
      .finally(() => setLoading(false));
  }, [slug, navigate]);

  const wishlisted = product ? isWishlisted(product.id) : false;

  const addToCart = () => {
    if (product.category.slug === 'juttis' && !selectedSize) {
      addToast('Please select a size.', 'error');
      return;
    }
    addToCartFn(product, qty, selectedSize);
    addToast(`${product.name} added to cart`, 'success');
    setAdded(true);
    setTimeout(() => setAdded(false), 2000);
  };

  const toggleWishlist = () => {
    toggleWishlistFn(product);
    addToast(
      wishlisted ? `${product.name} removed from wishlist` : `${product.name} saved to wishlist`,
      wishlisted ? 'info' : 'success'
    );
  };

  if (loading) return (
    <div style={{ textAlign: 'center', padding: 80, color: '#888', fontSize: 18 }}>Loading…</div>
  );
  if (!product) return null;

  const fallbackImage =
    `https://placehold.co/600x700/8B1A1A/gold?text=${encodeURIComponent(product.name.slice(0, 20))}`;
  const primaryImage = getProductImageUrl(product, fallbackImage);
  const galleryImages = [
    { id: 'primary', image: primaryImage, alt_text: product.name },
    ...(product.images || []),
  ].filter(
    (item, index, items) =>
      item.image && items.findIndex(candidate => candidate.image === item.image) === index
  );
  const displayedImage = selectedImage || primaryImage;

  const catSlug = product.category.slug;
  const titleSuffix = CATEGORY_TITLE_SUFFIX[catSlug] || 'Buy Online';
  const seoTitle = `${product.name} — ${titleSuffix} | A1 Looks`;
  const rawDesc = product.description
    ? product.description.slice(0, 140)
    : `Buy ${product.name} online at A1 Looks. Authentic handcrafted ethnic fashion`;
  const seoDescription = `${rawDesc}. Free shipping above ₹999. Shop at A1 Looks.`;
  const productJsonLd = {
    '@type': 'Product',
    name: product.name,
    description: product.description || seoDescription,
    image: primaryImage,
    sku: product.sku,
    mpn: product.sku,
    brand: { '@type': 'Brand', name: 'A1 Looks' },
    offers: {
      '@type': 'Offer',
      url: `${SITE_URL}/product/${product.slug}`,
      priceCurrency: 'INR',
      price: parseFloat(product.effective_price || product.price).toFixed(2),
      itemCondition: 'https://schema.org/NewCondition',
      availability: product.in_stock
        ? 'https://schema.org/InStock'
        : 'https://schema.org/OutOfStock',
      seller: { '@type': 'Organization', name: 'A1 Looks' },
    },
  };

  return (
    <div style={{ maxWidth: 1100, margin: '0 auto', padding: isMobile ? '24px 16px' : '40px 24px' }}>
      <SEOHead
        title={seoTitle}
        description={seoDescription}
        canonical={`${SITE_URL}/product/${product.slug}`}
        ogType="product"
        ogImage={primaryImage}
        jsonLd={productJsonLd}
      />
      <p style={{ color: '#999', fontSize: 13, marginBottom: 20 }}>
        <span onClick={() => navigate('/')} style={{ cursor: 'pointer', color: C.primary }}>Home</span>
        {' / '}
        <span onClick={() => navigate('/shop')} style={{ cursor: 'pointer', color: C.primary }}>Shop</span>
        {' / '}
        {product.name}
      </p>

      <div style={{ display: 'flex', gap: isMobile ? 0 : 48, flexDirection: isMobile ? 'column' : 'row', marginBottom: 60 }}>
        {/* Product image gallery */}
        <div style={{ width: isMobile ? '100%' : 'clamp(280px, 45%, 480px)', flexShrink: 0 }}>
          <img
            src={displayedImage}
            alt={product.name}
            style={{ width: '100%', aspectRatio: '4 / 5', objectFit: 'cover', borderRadius: 8, boxShadow: '0 4px 20px rgba(0,0,0,0.1)', display: 'block' }}
            onError={e => {
              e.currentTarget.onerror = null;
              e.currentTarget.src = fallbackImage;
            }}
          />

          {galleryImages.length > 1 && (
            <div
              style={{
                display: 'flex',
                gap: 10,
                marginTop: 12,
                overflowX: 'auto',
                paddingBottom: 4,
              }}
              aria-label="Product image gallery"
            >
              {galleryImages.map((item) => {
                const isSelected = displayedImage === item.image;
                return (
                  <button
                    key={item.id || item.image}
                    type="button"
                    onClick={() => setSelectedImage(item.image)}
                    aria-label={`View ${item.alt_text || product.name}`}
                    aria-pressed={isSelected}
                    style={{
                      width: isMobile ? 64 : 72,
                      height: isMobile ? 76 : 86,
                      padding: 0,
                      border: `2px solid ${isSelected ? C.primary : '#e0d0d0'}`,
                      borderRadius: 6,
                      background: '#fff',
                      overflow: 'hidden',
                      cursor: 'pointer',
                      flexShrink: 0,
                    }}
                  >
                    <img
                      src={item.image}
                      alt={item.alt_text || product.name}
                      loading="lazy"
                      style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
                      onError={e => {
                        e.currentTarget.onerror = null;
                        e.currentTarget.src = fallbackImage;
                      }}
                    />
                  </button>
                );
              })}
            </div>
          )}
        </div>

        <div style={{ flex: 1, minWidth: 0, marginTop: isMobile ? 24 : 0 }}>
          <p style={{ color: '#999', fontSize: 13, marginBottom: 8 }}>{product.category.name}</p>
          <h1 style={{ fontFamily: "'Playfair Display', serif", fontSize: isMobile ? 26 : 'clamp(24px, 3vw, 34px)', color: '#2C2C2C', marginBottom: 16, lineHeight: 1.3 }}>
            {product.name}
          </h1>

          <div style={{ display: 'flex', alignItems: 'baseline', gap: 12, marginBottom: 20, flexWrap: 'wrap' }}>
            <span style={{ fontSize: isMobile ? 24 : 28, fontWeight: 700, color: C.primary }}>
              ₹{Math.round(parseFloat(product.effective_price || product.price)).toLocaleString('en-IN')}
            </span>
            {product.sale_price && (
              <>
                <span style={{ fontSize: 18, color: '#999', textDecoration: 'line-through' }}>
                  ₹{Math.round(parseFloat(product.price)).toLocaleString('en-IN')}
                </span>
                <span style={{ background: '#e53935', color: '#fff', fontSize: 12, fontWeight: 700, padding: '2px 8px', borderRadius: 3 }}>
                  {Math.round((1 - product.sale_price / product.price) * 100)}% OFF
                </span>
              </>
            )}
          </div>

          <div style={{ display: 'flex', gap: 8, marginBottom: 20, flexWrap: 'wrap' }}>
            {product.is_new_arrival && <span style={{ background: C.gold, color: '#fff', fontSize: 11, fontWeight: 700, padding: '3px 10px', borderRadius: 12 }}>New Arrival</span>}
            {product.is_best_seller && <span style={{ background: C.primary, color: '#fff', fontSize: 11, fontWeight: 700, padding: '3px 10px', borderRadius: 12 }}>Best Seller</span>}
          </div>

          <p style={{ color: '#555', lineHeight: 1.8, fontSize: 15, marginBottom: 24 }}>{product.description}</p>

          {product.category.slug === 'juttis' && (
            <div style={{ marginBottom: 24 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
                <p style={{ fontWeight: 600, fontSize: 14, margin: 0 }}>Select Size (EU):</p>
                <button
                  onClick={() => setShowSizeGuide(g => !g)}
                  style={{ background: 'none', border: 'none', color: C.primary, fontSize: 13, cursor: 'pointer', fontWeight: 600, padding: 0, textDecoration: 'underline' }}
                >
                  {showSizeGuide ? 'Hide Size Guide' : 'Size Guide'}
                </button>
              </div>
              {showSizeGuide && (
                <div style={{ marginBottom: 14, borderRadius: 6, border: '1px solid #e0d0d0', overflow: 'hidden', fontSize: 13 }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                    <thead>
                      <tr style={{ background: C.primary, color: '#fff' }}>
                        <th style={{ padding: '8px 12px', textAlign: 'center', fontWeight: 700 }}>EU</th>
                        <th style={{ padding: '8px 12px', textAlign: 'center', fontWeight: 700 }}>Foot Length (cm)</th>
                        <th style={{ padding: '8px 12px', textAlign: 'center', fontWeight: 700 }}>Foot Length (in)</th>
                      </tr>
                    </thead>
                    <tbody>
                      {JUTTI_SIZE_GUIDE.map((row, i) => (
                        <tr key={row.eu} style={{ background: i % 2 === 0 ? '#fff' : '#FFF8F0' }}>
                          <td style={{ padding: '7px 12px', textAlign: 'center', fontWeight: selectedSize === row.eu ? 700 : 400, color: selectedSize === row.eu ? C.primary : '#2C2C2C' }}>{row.eu}</td>
                          <td style={{ padding: '7px 12px', textAlign: 'center', color: '#555' }}>{row.cm} cm</td>
                          <td style={{ padding: '7px 12px', textAlign: 'center', color: '#555' }}>{row.inch}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  <p style={{ fontSize: 11, color: '#888', padding: '6px 12px', margin: 0, background: '#fafafa' }}>Measure your foot from heel to toe. When between sizes, size up.</p>
                </div>
              )}
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                {JUTTI_SIZES.map(s => (
                  <button
                    key={s}
                    onClick={() => setSelectedSize(s)}
                    style={{
                      width: 44, height: 44,
                      border: `2px solid ${selectedSize === s ? C.primary : '#e0d0d0'}`,
                      background: selectedSize === s ? C.primary : '#fff',
                      color: selectedSize === s ? '#fff' : '#2C2C2C',
                      borderRadius: 4, cursor: 'pointer', fontSize: 14,
                      fontWeight: selectedSize === s ? 700 : 400,
                    }}
                  >{s}</button>
                ))}
              </div>
              <div style={{ marginTop: 12, display: 'inline-flex', alignItems: 'center', gap: 6, background: '#FFF8F0', border: '1px solid #D4A017', borderRadius: 20, padding: '5px 14px' }}>
                <span style={{ color: C.gold, fontSize: 15 }}>✦</span>
                <span style={{ fontSize: 12, fontWeight: 600, color: '#6d4c00' }}>Padded Sole — 100% Bite-Free Comfort Guarantee</span>
              </div>
            </div>
          )}

          <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 24, flexWrap: 'wrap' }}>
            <div style={{ display: 'flex', alignItems: 'center', border: '1.5px solid #e0d0d0', borderRadius: 4 }}>
              <button onClick={() => setQty(q => Math.max(1, q - 1))} style={{ width: 40, height: 44, border: 'none', background: 'none', fontSize: 18, cursor: 'pointer', color: C.primary }}>−</button>
              <span style={{ width: 36, textAlign: 'center', fontSize: 16, fontWeight: 600 }}>{qty}</span>
              <button onClick={() => setQty(q => q + 1)} style={{ width: 40, height: 44, border: 'none', background: 'none', fontSize: 18, cursor: 'pointer', color: C.primary }}>+</button>
            </div>
            <p style={{ fontSize: 13, color: product.in_stock ? '#2e7d32' : '#e53935', fontWeight: 600 }}>
              {product.in_stock ? `In Stock (${product.stock} left)` : 'Out of Stock'}
            </p>
          </div>

          <div style={{ display: 'flex', gap: 12, marginBottom: 0 }}>
            <button
              onClick={addToCart}
              disabled={!product.in_stock}
              style={{
                flex: 1,
                padding: '15px',
                background: added ? '#2e7d32' : product.in_stock ? C.primary : '#ccc',
                color: '#fff', border: 'none', borderRadius: 4,
                fontSize: isMobile ? 15 : 16, fontWeight: 700,
                cursor: product.in_stock ? 'pointer' : 'not-allowed',
                letterSpacing: 0.5, transition: 'background 0.3s',
              }}
            >
              {added ? '✓ Added to Cart!' : product.in_stock ? 'Add to Cart' : 'Out of Stock'}
            </button>
            <button
              onClick={toggleWishlist}
              title={wishlisted ? 'Remove from wishlist' : 'Save to wishlist'}
              style={{
                width: 52, padding: '15px',
                background: wishlisted ? C.primary : '#fff',
                color: wishlisted ? '#fff' : C.primary,
                border: `2px solid ${C.primary}`, borderRadius: 4,
                fontSize: 20, cursor: 'pointer',
                transition: 'background 0.2s, color 0.2s',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}
            >
              {wishlisted ? '♥' : '♡'}
            </button>
          </div>

          <div style={{ marginTop: 16, display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
            {[
              { icon: '💵', label: 'COD Available', sub: 'Pay on delivery' },
              { icon: '↩', label: '7-Day Exchange', sub: 'Easy returns & exchanges' },
              { icon: '🚚', label: 'Express Shipping', sub: 'Pan-India, 3–5 days' },
              { icon: '✓', label: 'Free Shipping', sub: 'On orders above ₹999' },
            ].map(b => (
              <div key={b.label} style={{ background: '#FFF8F0', border: '1px solid #f0e0e0', borderRadius: 6, padding: '10px 12px', display: 'flex', gap: 8, alignItems: 'center' }}>
                <span style={{ fontSize: 18, lineHeight: 1 }}>{b.icon}</span>
                <div>
                  <p style={{ fontSize: 12, fontWeight: 700, color: '#2C2C2C', margin: 0 }}>{b.label}</p>
                  <p style={{ fontSize: 11, color: '#888', margin: 0 }}>{b.sub}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {related.length > 0 && (
        <section>
          <h2 style={{ fontFamily: "'Playfair Display', serif", fontSize: isMobile ? 22 : 26, color: '#2C2C2C', marginBottom: 24 }}>
            You May Also Like
          </h2>
          <div style={{ display: 'grid', gridTemplateColumns: isMobile ? 'repeat(2, 1fr)' : 'repeat(auto-fill, minmax(220px, 1fr))', gap: isMobile ? 12 : 20 }}>
            {related.slice(0, 4).map(p => <ProductCard key={p.id} product={p} />)}
          </div>
        </section>
      )}

      <ReviewSection productId={product.id} />
    </div>
  );
}
